import { useAuthFetch } from "@/hooks/data/useAuthFetch";
import { leadSourceQueryKeys } from "@/hooks/data/v2/query-keys";
import { parseResponse } from "@/lib/query-helper";
import { APIResponse, PaginatedResponse, SearchFilter } from "@simplerealtybits/srb-types";
import { LeadSource } from "@t/LeadSource";
import { LeadSourceViewerDto } from "@t/LeadSourceViewerDto";
import { QueryClient, UndefinedInitialDataOptions, useQuery } from "@tanstack/react-query";
import { useCallback } from "react";

export const useGetLeadSourcesByFilter = (filter: SearchFilter | null) => {
    const authFetch = useAuthFetch();

    const getLeadSourcesByFilter = async (): Promise<PaginatedResponse<LeadSource>> => {
        if (filter === null) throw new Error("Filter is required");
        filter.pageSize = 1000; // Set a large page size to get all results
        return await authFetch("/api/v1/lead-sources/get-by-filter", {
            method: "POST",
            body: JSON.stringify(filter),
        }).then((response) => response.json());
    };

    const getLeadSourcesByFilterQueryOptions = () => ({
        queryKey: leadSourceQueryKeys.getByFilter(filter),
        queryFn: () => getLeadSourcesByFilter(),
        enabled: !!filter,
        select: (data: PaginatedResponse<LeadSource>) => data.data,
    });

    return useQuery(getLeadSourcesByFilterQueryOptions());
};

type UseGetLeadSourceForViewerOptions = UndefinedInitialDataOptions<
    APIResponse<LeadSourceViewerDto>,
    Error,
    LeadSourceViewerDto,
    readonly unknown[]
>;

export const useGetLeadSourceForViewer = (
    leadSourceId: number,
    options?: UseGetLeadSourceForViewerOptions,
    queryClient?: QueryClient
) => {
    const authFetch = useAuthFetch();

    const queryFn = useCallback(async (): Promise<APIResponse<LeadSourceViewerDto>> => {
        const response = await authFetch(`/api/v1/lead-sources/${leadSourceId}/viewer`);
        return await parseResponse(response);
    }, [authFetch, leadSourceId]);

    const getLeadSourceForViewerOptions = (
        leadSourceId?: number
    ): UseGetLeadSourceForViewerOptions => ({
        queryKey: leadSourceQueryKeys.getById(leadSourceId ?? 0),
        queryFn: queryFn,
        enabled: !!leadSourceId,
        select: (data: APIResponse<LeadSourceViewerDto>) => data.data,
    });

    return useQuery(
        {
            ...getLeadSourceForViewerOptions(leadSourceId),
            ...options,
        },
        queryClient
    );
};
